import { Icon } from 'antd';
import { Spin } from 'src/Library/AntdLibComponents';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import SEDocumentBar from './SEDocumentBar';
import { isValidArray } from 'src/Library/Helper';
import useDocument from 'src/hooks/form/useDocument';
import useFieldGroup from 'src/hooks/form/useFieldGroup';
import SEFieldGroup from '../../../SECommon/SEFieldGroup';
import SEFieldInput from '../../../SECommon/SEFieldInput';
import { documentTemplateType } from "./SEUploadDocumentsTaskDetails";
import { getFieldForDocumentTemplates } from 'src/api/shipmentExecution';
import { getGrpFieldsProps, parseFormValues } from '../../../SECommon/SEFieldGroupUtils';
import { useReduxState } from 'src/hooks/useRedux';
import injectSheet from 'react-jss';
import { set } from 'lodash';
import Asterisk from 'src/components/common/Asterisk';
import useDocumentParse from '../../../SECommon/hooks/useDocumentParse';
import { DocumentField } from 'src/dto/response/getFieldsForDocumentTemplate';

const styles = theme => ({
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  blackHeader: {
    color: theme.colors.text,
    fontSize: theme.fontSize.body,
    fontWeight: theme.fontWeight.semibold,
  },
  line: {
    display: 'flex',
    height: 1,
    backgroundColor: '#EDEDED',
    margin: `${theme.spacing.sm}px 0px`,
  },
  madatory: {
    color: 'red',
    fontSize: theme.fontSize.small,
    verticalAlign: 'text-top'
  },
  parseProgress: {
    display: 'flex',
    alignItems: 'center',
    margin: `${theme.spacing.sm}px 12px`,
  },
  parseProgressLabel: {
    color: theme.colors.textSecondary,
    fontSize: theme.fontSize.small,
    marginLeft: 8,
  },
  parseError: {
    color: theme.colors.error,
    fontSize: theme.fontSize.small,
    margin: `${theme.spacing.sm}px 12px`,
  },
});

const DocumentTemplate = (props) => {
  const {
    form,
    classes,
    sourceObjectId,
    taskId,
    templateOptions,
    taskUpdateActions,
    selectedTemplateKey,
    setIsUpdateDisabled,
    handleDeleteTemplate,
    setDocumentDetails,
    submitClicked,
    enableMultipleDuplicateValues,
  } = props;

  const selectedTemplate : documentTemplateType = templateOptions.find(template => template.key === selectedTemplateKey);

  const dropdownMasterValues = useReduxState([ 'shipmentExecutionReducer', 'dropdownMaster' ]).value;

  const document = useDocument({
    form,
    disabled: false,
    visible: true,
    label: '',
    multi: true,
    isMulti: true,
    accept: '.pdf, .jpg, .jpeg, .png, .xlsx, .xls, .csv, .doc, .docx,.msg, .eml',
  });
  const [documentFieldTemplateIds , setDocumentFieldTemplateIds] = useState<string[] | undefined>([]);
  const [documentField, setDocumentField] = useState<any[]>([{
    name: '',
    url: ''
  }]);
  const [listOfs3Files, setListOfs3Files] = useState<any[]>([]);
  /** Document parse & field prefill (ISS-292181). */
  const documentParse = useDocumentParse();
  /** The rendered field metadata, kept so prefill knows each field's type and multiplicity. */
  const [documentFields, setDocumentFields] = useState<DocumentField[]>([]);
  /**
   * Drives the attempt-unlock countdown. Ticks only while the limit is actually reached, at minute
   * granularity — the label is in whole minutes, so a faster clock would re-render for nothing.
   */
  const [now, setNow] = useState<number>(() => Date.now());
  /** Guards against re-triggering a parse for a document URL that was already sent. */
  const parsedDocUrl = useRef<string>('');
  /** Lets the effect below tell an "empty -> first document" transition from any other list change. */
  const uploadedDocsCount = useRef<number>(0);
  /** Keeps the field fetch to once per template — a refetch would rebuild the nodes and lose values. */
  const fetchedFieldsForTemplateId = useRef<string>('');
  useEffect(() => {
    setIsUpdateDisabled(document.isUploading);
  }, [document.isUploading]);

  useEffect(() => {
    if (submitClicked){
      setDocumentDetails(prevState => {
        return [...prevState, createApiCallObject()]
      });
      // setDocumentField([]);
    }
  }, [submitClicked])

  const documentTemplate = templateOptions?.find(template => template.key === selectedTemplateKey);
  const renderDeleteTemplate = (template: documentTemplateType) => {
    return (
      <Icon
        style={{
          marginLeft: 8,
        }}
        type="delete"
        onClick={() => {
          handleDeleteTemplate(template.key);
        }}
      />
    );
  }

  const renderLine = () => {
    return (
      <div className={classes.line} />
    );
  }

  const renderDocumentHeader = (documentTemplate: documentTemplateType | undefined) => {
    if (!documentTemplate) {
      return;
    }
    return (
      <div>
        <div className={classes.spaceBetween}>
          <div className={classes.blackHeader}>
            {documentTemplate?.label}
            <span className={classes.madatory}>{documentTemplate?.isMandatory ? <Asterisk /> : null}</span>
          </div>
          <div>
            {document.decorator(<SEFieldInput field={document} />)}
            {renderDeleteTemplate(documentTemplate)}
          </div>
        </div>
        {renderLine()}
      </div>
    );
  }

  const handleDeleteDoc = (fileToRemove) => {
    setListOfs3Files((prevFiles) =>
    prevFiles.filter((f) => f.s3Link !== fileToRemove.s3Link)
  );
};

const createApiCallObject = () => {
  // Collect field info if needed
  let fields;
  if (form.getFieldsValue().documentField) {
    fields = form.getFieldsValue().documentField
      .map((field, idx) => ({
        fieldTemplateId: idx.toString(),
        values: parseFormValues(field, enableMultipleDuplicateValues),
      }))
      .filter(
        (el) => Array.isArray(el.values)
          && documentFieldTemplateIds?.includes(el.fieldTemplateId)
      );
  }

  // Map each doc to get { name, url } from doc.name / doc.s3Link
  const documentDetails = documentField.map(doc => ({
    name: doc.name || '',
    url: doc.s3Link || '',
    ...(fields && { fields }),
  }));

  const documentTemplateId = selectedTemplate.id;
  const documentTemplateName = selectedTemplate.label;

  // Return one object in an array, with documentDetails as an array
  return (
    {
      documentDetails,
      documentTemplateId,
      documentTemplateName,
    });
};

useEffect(() => {
  const file = document.s3File;
  if (!file) return;

  if (
    typeof file.ext === "string" && file.ext.trim() !== "" &&
    typeof file.name === "string" && file.name.trim() !== "" &&
    typeof file.s3Link === "string" && file.s3Link.trim() !== ""
  ) {
    setListOfs3Files((prevFiles) => [...prevFiles, file]);
  }
}, [document.s3File]);
  const renderDocuments = (documentTemplate: documentTemplateType | undefined) => {
    // Show the list of files we've accumulated
    if (listOfs3Files.length > 0) {
      return listOfs3Files.map((file, index) => (
        <SEDocumentBar
          key={index}
          index={index}
          showTitle={false}
          onRemove={() => handleDeleteDoc(file)}  // adjust as needed
          isDeleteDisabled={document.isUploading} // if you still rely on document.isUploading
          containerBoxStyle={{ margin: '10px 12px' }}
          showColumnHeader={index === 0} 
          document={{
            updatedAt: moment(),
            documentType: documentTemplate?.label,
            name: file.name,
            url: file.s3Link,
          }}
        />
      ));
    }
  
    // No files? Return null or a placeholder.
    return null;
  };

  const [nodes, setNodes] = React.useState<any[]>([]);
  const fieldGroup = useFieldGroup({ defaultNodes: [] });

  /**
   * Every field on this template is filled against its document, so once the last document is removed
   * the values on screen describe nothing — parse-filled and hand-typed are wiped alike.
   *
   * Written as explicit `undefined` rather than `form.resetFields`, which would put each field back to
   * its initialValue instead of emptying it. Scoped to this template's own `fieldTemplateId`s because
   * every DocumentTemplate shares one form, and so one `documentField[...]` namespace.
   */
  const clearDocumentDependentFields = () => {
    if (!isValidArray(documentFields)) {
      return;
    }
    const currentValues = form.getFieldsValue()?.documentField;
    const valuesToClear = {};

    documentFields.forEach((field) => {
      const fieldKey = `documentField[${field.fieldTemplateId}]`;
      const currentValue = currentValues?.[field.fieldTemplateId];
      /** Multi-entry fields register one name per rendered row — clear the rows, not the array. */
      if (field.isMultipleEntryType) {
        if (isValidArray(currentValue)) {
          currentValue.forEach((_, index) => {
            valuesToClear[`${fieldKey}[${index}]`] = undefined;
          });
        }
        return;
      }
      valuesToClear[fieldKey] = undefined;
    });

    if (Object.keys(valuesToClear).length) {
      form.setFieldsValue(valuesToClear);
    }
  };

  /**
   * Everything that keys off "a document has been uploaded" — rendering this template's fields, and
   * triggering the parse (ISS-292181).
   *
   * Keyed on `listOfs3Files`, which gets one entry per COMPLETED upload (see the `document.s3File`
   * effect above), rather than on `document.value`. `document.value` cannot be trusted here: antd v3's
   * Upload calls `onChange` once per file synchronously when several files are picked at once, and
   * `handleCustomMultiRequest` appends to a state value it read before that batch — so a two-file pick
   * collapses `document.value` to just the last file, and the URL stamped on it in useDocument only
   * ever matches whichever upload finished first. It is also never pruned when a document is deleted.
   *
   * Fields are fetched once per template, on the first completed upload. Refetching would rebuild the
   * field nodes and wipe whatever the user (or the parse) has already put in them.
   *
   * The parse fires only when the list goes from EMPTY to holding a document, and only for that
   * document. Adding a second document to a list that already holds one triggers nothing — the fields
   * are already filled, or being filled, from the first. Clearing the list and uploading again does run
   * the whole flow (trigger -> polling -> prefill), so a user who removes the wrong file and uploads
   * the right one still gets it read.
   */
  useEffect(() => {
    const hadDocuments = uploadedDocsCount.current > 0;
    uploadedDocsCount.current = listOfs3Files.length;

    if (!listOfs3Files.length) {
      if (hadDocuments) {
        /**
         * Back to empty: stop any in-flight parse, drop the removed document's progress/error, empty
         * the fields it drove, and re-arm the trigger for the next upload. The fields stay rendered —
         * only their values go.
         */
        parsedDocUrl.current = '';
        documentParse.reset();
        clearDocumentDependentFields();
      }
      return;
    }

    if (fetchedFieldsForTemplateId.current !== selectedTemplate.id) {
      fetchedFieldsForTemplateId.current = selectedTemplate.id;
      handleGetFieldForTemplate(selectedTemplate.id);
    }

    if (hadDocuments) {
      return;
    }

    /** The upload is complete the moment we hold an S3 link, which is when the parse can start. */
    const url = listOfs3Files[0]?.s3Link;
    if (!url?.length || parsedDocUrl.current === url) {
      return;
    }
    parsedDocUrl.current = url;
    documentParse.startParse({
      shipmentId: sourceObjectId,
      taskId,
      docUrl: url,
      docType: selectedTemplate.label,
      documentTemplateId: selectedTemplate.id,
      /**
       * Read off the same entry as the URL, so the mime type always describes the document being
       * parsed. Reaches the agent verbatim, and is empty for a file the browser could not identify —
       * so nothing here needs a guess either way.
       */
      docMimeType: listOfs3Files[0]?.mimeType,
    });
  }, [listOfs3Files]);

  /**
   * Applies the parsed values to the on-screen form ONLY — nothing is persisted here. The user's own
   * typing always wins: an input that already holds a value is never overwritten, because the parse
   * runs for several seconds and they may well have filled it in the meantime.
   *
   * Waits on `nodes` because antd v3 discards setFieldsValue for a key that getFieldDecorator has
   * not registered yet.
   */
  useEffect(() => {
    if (!isValidArray(documentParse.extractedFields) || !isValidArray(nodes)) {
      return;
    }
    const valuesToApply = {};
    documentParse.extractedFields.forEach((extracted) => {
      const fieldMeta = documentFields.find(
        (field) => field.fieldTemplateId?.toString() === extracted.fieldTemplateId?.toString()
      );
      if (!fieldMeta) {
        return;
      }
      /** Multi-entry fields render their first row under an index suffix (see useMultiField). */
      const formKey = fieldMeta.isMultipleEntryType
        ? `documentField[${fieldMeta.fieldTemplateId}][0]`
        : `documentField[${fieldMeta.fieldTemplateId}]`;

      const currentValue = form.getFieldValue(formKey);
      const isEmpty =
        currentValue === undefined ||
        currentValue === null ||
        currentValue === '' ||
        (Array.isArray(currentValue) && !currentValue.length);
      if (!isEmpty) {
        return;
      }
      /** DATE arrives as epoch ms; the DatePicker takes a moment, same as saved values are hydrated. */
      valuesToApply[formKey] = fieldMeta.type === 'DATE' ? moment(extracted.value) : extracted.value;
    });

    if (Object.keys(valuesToApply).length) {
      form.setFieldsValue(valuesToApply);
    }
  }, [documentParse.extractedFields, nodes, documentFields]);

  /**
   * Keeps the attempt-unlock countdown current. Only runs while the limit is reached and the unlock is
   * still in the future, so the common case installs no interval at all.
   */
  useEffect(() => {
    if (!documentParse.isAttemptLimitReached || !documentParse.attemptsResetAt) {
      return undefined;
    }
    const resetAt = documentParse.attemptsResetAt;
    setNow(Date.now());
    if (resetAt <= Date.now()) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      // Nothing left to count down to; stop rather than re-rendering once a minute forever.
      if (current >= resetAt) {
        window.clearInterval(timer);
      }
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, [documentParse.isAttemptLimitReached, documentParse.attemptsResetAt]);

 useEffect(() => {
  setDocumentField(listOfs3Files)
 }, [listOfs3Files])
  //setDocumentField
  useEffect(() => {
    fieldGroup.setNodes([...nodes]);
  }, [nodes]);

  const handleGetFieldForTemplate = async (documentTemplateId: string | undefined) => {
    if (!documentTemplateId) {
      return;
    }

    const response = await getFieldForDocumentTemplates({
      id: sourceObjectId,
      documentTemplateId,
    });

    setDocumentFieldTemplateIds((response.data?.fields)?.map((field) => field.fieldTemplateId));
    setDocumentFields(response.data?.fields || []);

    if (response.isSuccess) {
      const { data } = response;
      if (data) {
        const fields = getGrpFieldsProps({
          form,
          fields: data.fields,
          keyCB: (key) => `documentField[${key}]`,
          defaultDisabled: false,
        }) || [];
        setNodes(fields);
      } else {
        setNodes([]);
      }
    }
  }

  const renderRelatedFields = (documentTemplate: documentTemplateType | undefined) => {
    if (!documentTemplate) {
      return;
    }

    if (!isValidArray(nodes)) {
      return (
        <div style={{ marginBottom: 80 }}>

        </div>
      );
    }

    return (
      <SEFieldGroup
        fieldGroup={fieldGroup}
        dropdownMasterValues={dropdownMasterValues}
      />
    );
  }

  /**
   * Parse in flight (ISS-292181). Indeterminate on purpose: the agent reports no progress, so there
   * is nothing to drive a percentage from and a creeping bar would only invent one.
   * Parsing being unavailable for this task or document type is silent by design: the user just
   * fills the form as before.
   */
  const renderParseProgress = () => {
    if (!documentParse.isParsing) {
      return null;
    }
    return (
      <div className={classes.parseProgress}>
        <Spin size="small" />
        <div className={classes.parseProgressLabel}>Reading document and filling the fields below…</div>
      </div>
    );
  };

  /**
   * Every attempt in the window is used. Not a failure — the window releases a slot on its own — so
   * this shows the wait rather than an error, refreshed each minute off `attemptsResetAt`.
   */
  const renderParseAttemptLimit = () => {
    if (!documentParse.isAttemptLimitReached) {
      return null;
    }
    const millisecondsToWait = (documentParse.attemptsResetAt || 0) - now;
    // Round UP so the message never invites a retry a moment before the slot has actually freed.
    const minutesToWait = millisecondsToWait > 0 ? Math.ceil(millisecondsToWait / (60 * 1000)) : 0;
    const attemptsAllowed = documentParse.attemptsAllowed;
    const usedUp = attemptsAllowed
      ? `All ${attemptsAllowed} document parsing attempts for this document are used.`
      : 'All document parsing attempts for this document are used.';
    const unlock = minutesToWait
      ? ` Another attempt unlocks in ${minutesToWait} minute${minutesToWait === 1 ? '' : 's'}.`
      : ' Another attempt is available now — re-upload to try again.';
    return (
      <div className={classes.parseError}>
        {usedUp}
        {unlock} Please enter the values manually.
      </div>
    );
  };

  /**
   * A parse that started and then failed — agent error, timed out, or the submit never landed. The
   * hook keeps the trigger leg silent, so anything reaching here is worth showing: without it the
   * spinner simply vanishes and the user is left staring at empty inputs with no explanation.
   * The attempt limit is not one of these: it has its own renderer and leaves `errorMessage` empty.
   */
  const renderParseError = () => {
    if (documentParse.isParsing || !documentParse.errorMessage) {
      return null;
    }
    return <div className={classes.parseError}>{documentParse.errorMessage}</div>;
  };

  return (
    <>
      {renderDocumentHeader(documentTemplate)}
      {renderDocuments(documentTemplate)}
      {renderParseProgress()}
      {renderParseAttemptLimit()}
      {renderParseError()}
      {renderRelatedFields(documentTemplate)}
    </>
  );
}

export default injectSheet(styles)(DocumentTemplate);